import { usePathname } from 'next/navigation';
import classNames from 'classnames';
import GoogleSchema from '@/components/google-schema';
import { IconArrowSquareLeft } from '@/components/svg-symbols/icons';
import { useBasicMetas } from '@/app/(plp)/_utils/use-basic-metas';
import { usePLPFacetsContext } from '@/app/(plp)/facets-context/facets-context';
import { PLPPages } from '@/app/(plp)/types';
import { PUBLIC_DOMAIN_URL } from '@/app/_constants/urls';
import { PLP_PAGES_WITH_BREADCRUMB } from './constants';
import { createBreadCrumbItems } from './utils/create-breadcrumb-items';
import { getBaseURL } from './utils/url-helpers';
import { AppNextLink } from '../app-next-link';
interface Props {
  className?: string;
}
function Breadcrumb({
  className
}: Props) {
  const pathname = usePathname();
  const {
    page
  } = usePLPFacetsContext();
  const {
    basicMetas,
    isLoadingBasicMetas
  } = useBasicMetas();
  if (!page || !PLP_PAGES_WITH_BREADCRUMB.includes(page)) return null;
  const items = createBreadCrumbItems(page, basicMetas);
  if (!items) return null;
  if (isLoadingBasicMetas) return <div className='mb-3 mt-16 h-5 w-full animate-pulse rounded bg-gray-100 lg:mt-0 lg:w-64' />;
  const populatedItems = items.map(({
    nameFa
  }, index) => {
    const lastIndex = items.length - 1;
    let currentPage = page;
    if (page === PLPPages['Combination']) {
      currentPage = index === lastIndex ? PLPPages.Brands : PLPPages.Categories;
    } else if (page === PLPPages['BrandCategoriesCombination']) {
      currentPage = index === lastIndex ? PLPPages.BrandCategoriesCombination : PLPPages.Categories;
    }
    return {
      title: nameFa,
      url: index === lastIndex ? pathname : `/${getBaseURL({
        page: currentPage,
        basicMetas: {
          breadcrumb: items.slice(0, index + 1)
        }
      })}`
    };
  });
  if (populatedItems.length < 2) return null;

  // Breadcrumb SEO Schema Data
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: populatedItems.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.title,
      ...(i + 1 !== items.length && {
        item: `${PUBLIC_DOMAIN_URL}${item.url}`
      })
    }))
  };
  return <>
            <GoogleSchema schema={breadcrumbSchema} data-sentry-element="GoogleSchema" data-sentry-source-file="breadcrumb.tsx" />

            <div data-test='breadcrumb' className={classNames('hide-scrollbar col-span-full mb-3 flex items-center overflow-x-auto text-xs lg:mb-4 lg:text-sm', className)}>
                {populatedItems.map((item, i) => <span key={item.title} className='flex shrink-0 items-center gap-0.5 text-caption-12 text-text-darkGray lg:text-body-14'>
                        {populatedItems.length === i + 1 ? <span className='cursor-default'>{item.title}</span> : <AppNextLink prefetch={false} href={item.url}>
                                {item.title}
                            </AppNextLink>}

                        {populatedItems.length !== i + 1 && <IconArrowSquareLeft className='h-4 w-4 lg:h-5 lg:w-5' />}
                    </span>)}
            </div>
        </>;
}
export default Breadcrumb;